import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { predictionsAPI, alertsAPI, candlePredictionsAPI, cryptoAPI, chatbotAPI } from '../../../services/api';
import './Chatbot.css';

interface Message {
  id: number;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

interface UserData {
  predictions: any[];
  alerts: any[];
  candlePredictions: any[];
}

const Chatbot: React.FC = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [userData, setUserData] = useState<UserData>({
    predictions: [],
    alerts: [],
    candlePredictions: []
  });
  const [dataLoaded, setDataLoaded] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [initializedFromStorage, setInitializedFromStorage] = useState(false);

  const getStorageKey = () => {
    const userId = (user as any)?.id ?? 'guest';
    return `crypxa_chatbot_history_${userId}`;
  };

  const loadUserData = async () => {
    try {
      const [predictionsRes, alertsRes, candleRes] = await Promise.all([
        predictionsAPI.getAll().catch(() => ({ data: [] })),
        alertsAPI.getAll().catch(() => ({ data: [] })),
        candlePredictionsAPI.getAll().catch(() => ({ data: [] }))
      ]);

      setUserData({
        predictions: predictionsRes.data || [],
        alerts: alertsRes.data || [],
        candlePredictions: candleRes.data || []
      });
      setDataLoaded(true);
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  // Load user data when component mounts or chat opens
  useEffect(() => {
    if (isOpen && user && !dataLoaded) {
      loadUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user]);

  // Load chat history from session storage
  useEffect(() => {
    if (initializedFromStorage) return;
    if (typeof window === 'undefined') return;

    try {
      const stored = sessionStorage.getItem(getStorageKey());
      if (stored) {
        const parsed = JSON.parse(stored) as Array<Omit<Message, 'timestamp'> & { timestamp: string }>;
        const restored: Message[] = parsed.map(m => ({
          ...m,
          timestamp: new Date(m.timestamp),
        }));
        if (restored.length > 0) {
          setMessages(restored);
        }
      }
    } catch (err) {
      console.error('Failed to load chatbot history from session:', err);
    } finally {
      setInitializedFromStorage(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, initializedFromStorage]);

  // Persist chat history to session storage
  useEffect(() => {
    if (!initializedFromStorage) return;
    if (typeof window === 'undefined') return;

    try {
      const serializable = messages.map(m => ({
        ...m,
        timestamp: m.timestamp.toISOString(),
      }));
      sessionStorage.setItem(getStorageKey(), JSON.stringify(serializable));
    } catch (err) {
      console.error('Failed to save chatbot history to session:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, user, initializedFromStorage]);

  // Initialize welcome message if no history
  useEffect(() => {
    if (user && initializedFromStorage && messages.length === 0) {
      const userName = user.first_name || user.username || 'Trader';
      setMessages([{
        id: 1,
        text: `Welcome ${userName}! 👋\n\nI'm your AI-powered trading advisor. I've analyzed your portfolio using advanced market data and technical analysis. I'm here to help you make informed trading decisions.\n\nWhat would you like to know?`,
        sender: 'bot',
        timestamp: new Date()
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, initializedFromStorage]);

  // Auto scroll to bottom when new message arrives
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const userMessage: Message = {
      id: messages.length + 1,
      text: inputMessage.trim(),
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    // Generate bot response (may include async operations)
    try {
      const response = await generateBotResponseAsync(userMessage.text);
      const botResponse: Message = {
        id: messages.length + 2,
        text: response,
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botResponse]);
    } catch (error) {
      const botResponse: Message = {
        id: messages.length + 2,
        text: `Sorry, I encountered an error. Please try again.`,
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botResponse]);
    } finally {
      setIsTyping(false);
    }
  };

  const generateBotResponseAsync = async (userText: string): Promise<string> => {
    const text = userText.toLowerCase().trim();
    const userName = user?.first_name || user?.username || 'Trader';
    const originalText = userText.trim();
    
    // Count Questions - Handle "how many", "how much", "count" questions
    if ((text.includes('how many') || text.includes('how much') || text.includes('count')) && 
        (text.includes('prediction') || text.includes('alert') || text.includes('candle'))) {
      
      const totalPreds = userData.predictions.length;
      const activePreds = userData.predictions.filter(p => {
        const targetDate = new Date(p.target_date);
        return targetDate > new Date();
      });
      const alertCount = userData.alerts.filter(a => a.status === 'active').length;
      const totalAlerts = userData.alerts.length;
      const candleCount = userData.candlePredictions.length;
      
      if (text.includes('prediction')) {
        if (text.includes('active') || text.includes('current')) {
          return `${userName}, you have ${activePreds.length} active prediction${activePreds.length !== 1 ? 's' : ''}.\n\nTotal predictions created: ${totalPreds}`;
        }
        return `${userName}, you have created ${totalPreds} prediction${totalPreds !== 1 ? 's' : ''} in total.\n\nActive predictions: ${activePreds.length}`;
      }
      
      if (text.includes('alert')) {
        if (text.includes('active') || text.includes('current')) {
          return `${userName}, you have ${alertCount} active alert${alertCount !== 1 ? 's' : ''}.\n\nTotal alerts created: ${totalAlerts}`;
        }
        return `${userName}, you have created ${totalAlerts} alert${totalAlerts !== 1 ? 's' : ''} in total.\n\nActive alerts: ${alertCount}`;
      }
      
      if (text.includes('candle')) {
        return `${userName}, you have created ${candleCount} candle prediction${candleCount !== 1 ? 's' : ''}.`;
      }
      
      // General count
      return `${userName}, here's your count:\n\n• Predictions: ${totalPreds} (${activePreds.length} active)\n• Alerts: ${totalAlerts} (${alertCount} active)\n• Candle Analyses: ${candleCount}`;
    }
    
    // Exact question matching - be very specific
    // Portfolio Analysis - only if explicitly asked
    if ((text.includes('portfolio') && (text.includes('my') || text.includes('show') || text.includes('analyze'))) || 
        (text.includes('my predictions') && (text.includes('show') || text.includes('list') || text.includes('see'))) ||
        (text.includes('my alerts') && (text.includes('show') || text.includes('list') || text.includes('see'))) ||
        (text.includes('my data') && text.includes('show'))) {
      const predCount = userData.predictions.length;
      const alertCount = userData.alerts.filter(a => a.status === 'active').length;
      const candleCount = userData.candlePredictions.length;
      
      if (predCount === 0 && alertCount === 0 && candleCount === 0) {
        return `Hey ${userName}! 👋\n\nYou're just getting started - that's great! Here's a strategic approach:\n\n• Start with 2-3 high-confidence predictions (aim for 70%+ confidence)\n• Set alerts on BTC/ETH first - they're your foundation\n• Use candle analysis to time your entries\n\nRemember: Quality over quantity. Focus on high-probability setups.\n\nWant help creating your first prediction?`;
      }
      
      const activePreds = userData.predictions.filter(p => {
        const targetDate = new Date(p.target_date);
        return targetDate > new Date();
      });
      
      let response = `Here's your portfolio snapshot, ${userName}:\n\n`;
      response += `📊 Active Predictions: ${activePreds.length}\n`;
      response += `🔔 Active Alerts: ${alertCount}\n`;
      response += `🕯️ Candle Analyses: ${candleCount}\n\n`;
      
      if (activePreds.length > 0) {
        const avgConfidence = activePreds.reduce((sum, p) => sum + parseFloat(p.confidence_score || '0'), 0) / activePreds.length;
        
        if (avgConfidence > 75) {
          response += `Your average confidence is ${avgConfidence.toFixed(0)}% - excellent! 🎯\n\nRecommended strategy: Take profits at 2x-3x targets. Implement trailing stops to protect gains.`;
        } else if (avgConfidence > 60) {
          response += `Average confidence: ${avgConfidence.toFixed(0)}% - moderate setup. ⚠️\n\nRecommended strategy: Use tight stops (5-7%) and take partial profits early. Risk management is key.`;
        } else {
          response += `Confidence is ${avgConfidence.toFixed(0)}% - below optimal threshold. 🔴\n\nRecommended strategy: Wait for higher confidence setups. Patience and discipline are essential.`;
        }
      }
      
      return response;
    }
    
    // Spot Trading - only if explicitly asked about spot trading
    if ((text.includes('spot') && (text.includes('trading') || text.includes('trade') || text.includes('what') || text.includes('how'))) ||
        (text.includes('buy and hold') && (text.includes('what') || text.includes('how') || text.includes('strategy')))) {
      return `Spot trading strategy, ${userName}:\n\n**Entry Strategy:**\n• Buy on dips (15-20% corrections)\n• Enter when RSI is below 40 (oversold)\n• Enter at established support levels\n• Avoid chasing pumps\n\n**Position Sizing:**\n• Maximum 2% risk per trade\n• Use DCA (Dollar Cost Averaging) for larger positions\n• Maintain 30% cash reserve\n\n**Exit Strategy:**\n• Take 50% profits at 2x target\n• Let remaining 50% run with trailing stop\n• Re-enter on pullbacks to support\n\n**Analysis:** Spot trading in bull markets offers lower risk and better sleep quality. Compound growth is more sustainable.\n\nNeed entry points for a specific coin?`;
    }
    
    // Futures Trading - only if explicitly asked about futures
    if ((text.includes('futures') && (text.includes('trading') || text.includes('trade') || text.includes('what') || text.includes('how'))) ||
        (text.includes('leverage') && (text.includes('trading') || text.includes('how') || text.includes('what'))) ||
        ((text.includes('long') || text.includes('short')) && (text.includes('position') || text.includes('trade') || text.includes('futures')))) {
      return `Futures trading strategy, ${userName}:\n\n**Leverage Management:**\n• Beginners: 3-5x maximum\n• Advanced: 10x absolute maximum\n• Never go all-in on a single position\n\n**Risk Management:**\n• Stop-loss: 2-3% from entry\n• Take profits: 5-10% (scale out)\n• Never add to losing positions\n\n**Market Conditions:**\n• Long positions in confirmed uptrends\n• Short positions in confirmed downtrends\n• Avoid choppy/sideways markets\n\n**Entry Timing:**\n• Enter on breakouts with high volume\n• Exit before major news events\n• Use 4H/1D timeframes for trend direction\n\n**Important:** Futures amplify both gains and losses. Maintain strict discipline and avoid emotional trading.\n\nGot a specific setup you want analyzed?`;
    }
    
    // Entry/Exit Points - only if explicitly asked
    if ((text.includes('entry') && (text.includes('point') || text.includes('when') || text.includes('where'))) ||
        text.includes('when to buy') || text.includes('when to sell') ||
        (text.includes('exit') && (text.includes('point') || text.includes('when') || text.includes('where')))) {
      return `Entry and exit strategy framework, ${userName}:\n\n**Optimal Entry Conditions:**\n• Breakout above resistance with high volume\n• RSI crosses 50 from oversold territory\n• MACD bullish crossover confirmation\n• Bounce off established support levels\n• Your predictions show 70%+ confidence\n\n**Exit Strategy:**\n• Take 30% profits at 1.5x target\n• Take 40% profits at 2x target\n• Let remaining 30% run with trailing stop\n• Exit if confidence drops below 60%\n• Stop-loss: -7% (spot) or -3% (futures)\n\n**Key Principle:** Systematic profit-taking protects gains. Greed can turn winners into losers.\n\nWhich coin? I'll analyze your specific setup.`;
    }
    
    // Risk Management - only if explicitly asked
    if ((text.includes('risk') && (text.includes('management') || text.includes('manage') || text.includes('how') || text.includes('what'))) ||
        (text.includes('stop loss') || text.includes('stop-loss')) ||
        (text.includes('risk') && text.includes('control'))) {
      return `Risk management framework, ${userName}:\n\n**Position Sizing:**\n• Spot trading: Risk 1-2% per trade\n• Futures trading: Risk 0.5-1% per trade\n• Maximum 5 concurrent positions\n\n**Stop-Loss Strategy:**\n• Spot: 7-10% below entry\n• Futures: 2-3% below entry\n• Never move stop-loss against your position\n• Use ATR (Average True Range) for volatile coins\n\n**Portfolio Management:**\n• Maximum 10% total portfolio risk\n• Maintain 20-30% cash reserve\n• Diversify across different sectors\n\n**Trading Discipline:**\n• Avoid trading after significant losses\n• No revenge trading\n• Stick to your trading plan\n\n**Core Principle:** The goal is consistent profitability, not being right. Cut losses quickly, let winners run.\n\nWant me to review your current positions?`;
    }
    
    // Profit Taking - only if explicitly asked
    if ((text.includes('profit') && (text.includes('taking') || text.includes('take') || text.includes('when') || text.includes('how'))) ||
        text.includes('take profit') ||
        (text.includes('when to sell') && text.includes('profit')) ||
        (text.includes('sell') && (text.includes('when') || text.includes('profit')))) {
      const activePreds = userData.predictions.filter(p => {
        const targetDate = new Date(p.target_date);
        return targetDate > new Date();
      });
      
      if (activePreds.length > 0) {
        let response = `Here's your profit plan, ${userName}:\n\n**Your active positions:**\n`;
        activePreds.slice(0, 3).forEach(pred => {
          const currentPrice = parseFloat(pred.current_price);
          const predictedPrice = parseFloat(pred.predicted_price);
          const profit = ((predictedPrice - currentPrice) / currentPrice * 100).toFixed(1);
          response += `\n• ${pred.coin_symbol}: ${profit}% target\n`;
        });
        response += `\n**Profit-Taking Strategy:**\n• 30% at 50% of target\n• 40% at full target\n• 30% runs with trailing stop\n• Exit if confidence drops below threshold\n\n**Key Principle:** Systematic profit-taking protects gains. Taking profits is never wrong.`;
        return response;
      }
      
      return `Profit-taking framework, ${userName}:\n\n**Scale-Out Strategy:**\n• 30% at 1.5x (lock in capital + 50% profit)\n• 40% at 2x (protect initial capital)\n• 30% runs with trailing stop\n• Move stop to breakeven after 2x target\n• Trail stop at 20% below peak price\n\n**Critical Rule:** Never let a winner become a loser. If position is up 50%+ and drops to 20%, take profits. Greed can erode gains.\n\nWant me to analyze your current positions?`;
    }
    
    // Market Analysis - only if explicitly asked
    if ((text.includes('market') && (text.includes('analysis') || text.includes('analyze') || text.includes('trend') || text.includes('how'))) ||
        (text.includes('trend') && (text.includes('analysis') || text.includes('what') || text.includes('current'))) ||
        ((text.includes('bull') || text.includes('bear')) && (text.includes('market') || text.includes('trend'))) ||
        (text.includes('analysis') && (text.includes('market') || text.includes('trend')))) {
      return `Market analysis framework, ${userName}:\n\n**Trend Identification:**\n• Higher highs + higher lows = Uptrend (buy on dips)\n• Lower highs + lower lows = Downtrend (sell on rallies)\n• Sideways = Wait for breakout confirmation\n\n**Volume Analysis:**\n• Breakouts with high volume = Strong move\n• Low volume breakouts = Potential fake outs\n• Volume typically precedes price movement\n\n**Support/Resistance:**\n• Buy at support levels, sell at resistance\n• Breakout above resistance = Bullish signal\n• Breakdown below support = Bearish signal\n\n**Market Sentiment:**\n• Fear & Greed Index < 20 = Accumulation zone\n• Fear & Greed Index > 80 = Distribution zone\n• Extreme fear = Potential buying opportunity\n\n**Key Principle:** Trade based on price action and technical analysis, not emotions or opinions.\n\nWhat timeframe are you analyzing?`;
    }
    
    // Specific Coin Advice - only if coin name/symbol is mentioned
    const coinSymbols = ['btc', 'bitcoin', 'eth', 'ethereum', 'bnb', 'sol', 'ada', 'xrp', 'doge', 'dot', 'matic', 'avax', 'link', 'uni', 'ltc'];
    const hasCoinSymbol = coinSymbols.some(symbol => text.includes(symbol)) || text.match(/\b[A-Z]{2,5}\b/);
    
    if (hasCoinSymbol && (text.includes('should') || text.includes('buy') || text.includes('sell') || text.includes('predict') || text.includes('analysis') || text.includes('advice') || text.includes('price') || text.includes('target'))) {
      const coinMatch = text.match(/\b([A-Z]{2,5})\b/) || 
                       (text.includes('bitcoin') ? ['BTC'] : null) ||
                       (text.includes('ethereum') ? ['ETH'] : null);
      
      if (coinMatch) {
        const coinSymbol = coinMatch[1].toUpperCase();
        const userPreds = userData.predictions.filter(p => 
          p.coin_symbol.toUpperCase() === coinSymbol
        );
        
        if (userPreds.length > 0) {
          const latestPred = userPreds[0];
          const confidence = parseFloat(latestPred.confidence_score);
          const currentPrice = parseFloat(latestPred.current_price);
          const predictedPrice = parseFloat(latestPred.predicted_price);
          const potentialGain = ((predictedPrice - currentPrice) / currentPrice * 100).toFixed(1);
          
          let response = `${coinSymbol} analysis, ${userName}:\n\n`;
          response += `Current: $${currentPrice.toFixed(2)}\n`;
          response += `Target: $${predictedPrice.toFixed(2)} (${potentialGain}%)\n`;
          response += `Confidence: ${confidence}%\n\n`;
          
          if (confidence > 75) {
            response += `High confidence setup ✅\n\n`;
            response += `**Plan:**\n• Entry: Current or 5% dip\n• Stop: 7% below entry\n• Take profit: 1.5x, 2x, 2.5x\n• Size: 2-3% of portfolio\n\n`;
            response += `This looks solid. Consider a slightly larger position if risk allows.`;
          } else if (confidence > 60) {
            response += `Moderate confidence ⚠️\n\n`;
            response += `**Plan:**\n• Entry: Wait for support pullback\n• Stop: Tight at 5%\n• Take profit: Quick 1.5x\n• Size: 1-2% of portfolio\n\n`;
            response += `Smaller position here. Protect capital first.`;
          } else {
            response += `Low confidence 🔴\n\n`;
            response += `**Recommendation:** Wait for better setup. Look for additional confirmation signals. Consider other coins with higher confidence scores.\n\n`;
            response += `Key principle: Patience and discipline are essential. Not every setup requires action.`;
          }
          
          return response;
        }
      }
      
      return `For coin analysis, ${userName}:\n\n**My process:**\n1. Check your predictions (confidence, targets)\n2. Technical setup (support/resistance, volume, RSI/MACD)\n3. Entry strategy (pullback to support, breakout with volume)\n\n**Tip:** Create a prediction first, then I'll give you specific entry/exit points.\n\nWhich coin?`;
    }
    
    // Greetings - only exact greetings
    if ((text === 'hello' || text === 'hi' || text === 'hey' || text === 'hey there' || text === 'hello there') && text.length < 15) {
      return `Hey ${userName}! 👋\n\nI'm your AI-powered trading advisor. I've analyzed your portfolio using advanced market data and technical analysis. I'm here to help you make informed trading decisions.\n\nWhat would you like to know?`;
    }
    
    // Help - only if explicitly asked
    if ((text.includes('help') && (text.includes('me') || text.length < 10)) || 
        (text.includes('what can you do') || text.includes('what do you do'))) {
      return `I'm your trading coach, ${userName}. I can help with:\n\n• Portfolio analysis\n• Trading strategies (spot & futures)\n• Entry/exit points\n• Risk management\n• Profit-taking\n• Coin-specific advice\n\nWhat do you need help with?`;
    }
    
    // SIMPLE PRICE CHECK - Must come BEFORE prediction logic
    // Use API to dynamically fetch coin data
    if ((text.includes('check') || text.includes('what is') || text.includes('tell me') || text.includes('current')) && 
        text.includes('price') && (text.includes('coin') || text.includes('crypto'))) {
      
      // Extract potential coin symbol/name from text
      let coinSymbol = '';
      const coinMatch = text.match(/\b([A-Z]{2,10})\b/); // Match uppercase symbols (2-10 chars for longer names)
      if (coinMatch) {
        coinSymbol = coinMatch[1].toUpperCase();
      } else {
        // Try to extract from common patterns like "sui coin", "bitcoin price", etc.
        const words = text.split(/\s+/);
        for (const word of words) {
          const cleanWord = word.toLowerCase().replace(/[^a-z]/g, '');
          if (cleanWord.length >= 2 && cleanWord.length <= 10 && 
              !['price', 'coin', 'crypto', 'check', 'what', 'is', 'tell', 'me', 'current', 'the', 'of'].includes(cleanWord)) {
            coinSymbol = cleanWord.toUpperCase();
            break;
          }
        }
      }
      
      if (coinSymbol && coinSymbol.length >= 2) {
        try {
          const quoteResponse = await cryptoAPI.getQuotes(coinSymbol);
          if (quoteResponse.data?.data?.[coinSymbol]) {
            const coinData = Array.isArray(quoteResponse.data.data[coinSymbol]) 
              ? quoteResponse.data.data[coinSymbol][0] 
              : quoteResponse.data.data[coinSymbol];
            const price = coinData.quote?.USD?.price || 0;
            const change24h = coinData.quote?.USD?.percent_change_24h || 0;
            const coinName = coinData.name || coinSymbol;
            return `${userName}, ${coinName} (${coinSymbol}) current price: $${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })}\n\n24h Change: ${change24h > 0 ? '+' : ''}${change24h.toFixed(2)}%`;
          }
        } catch (error) {
          // If direct symbol lookup fails, let GPT-4 handle it (it knows about coins)
          // Fall through to GPT-4
        }
      }
    }
    
    // Price Prediction Requests - Handle expert predictions (only if explicitly asking for prediction)
    if ((text.includes('predict') || text.includes('prediction') || text.includes('forecast') || text.includes('expect') || text.includes('will be') || (text.includes('after') && text.includes('month'))) && 
        (text.includes('price') || text.includes('coin') || text.includes('crypto') || text.includes('token'))) {
      
      // Extract coin symbol - use API dynamically, no static mapping
      let coinSymbol = '';
      
      // First try to match uppercase coin symbols (2-10 letters for longer names)
      const uppercaseMatch = text.match(/\b([A-Z]{2,10})\b/);
      if (uppercaseMatch) {
        coinSymbol = uppercaseMatch[1].toUpperCase();
      } else {
        // Try to extract coin name/symbol from text patterns
        // Look for words before "coin", "crypto", "token", "price", "prediction"
        const wordMatch = text.match(/\b([a-z]{2,15})\s+(?:coin|crypto|token|price|prediction|forecast)/i);
        if (wordMatch) {
          coinSymbol = wordMatch[1].toUpperCase();
        } else {
          // Extract any significant word that might be a coin name
          const words = text.split(/\s+/);
          for (const word of words) {
            const cleanWord = word.toLowerCase().replace(/[^a-z]/g, '');
            if (cleanWord.length >= 2 && cleanWord.length <= 15 && 
                !['price', 'coin', 'crypto', 'token', 'predict', 'prediction', 'forecast', 'expect', 'will', 'be', 'after', 'next', 'the', 'of', 'a', 'an'].includes(cleanWord)) {
              coinSymbol = cleanWord.toUpperCase();
              break;
            }
          }
        }
      }
      
      // Extract time period
      let timePeriod = '';
      let hoursAhead = 0;
      if (text.match(/(\d+)\s*(?:month|months)/i)) {
        // For months, use GPT-4 for better analysis
        try {
          const gptResponse = await chatbotAPI.query(originalText);
          if (gptResponse.data?.response) {
            return gptResponse.data.response;
          }
        } catch (error) {
          console.error('GPT API error:', error);
        }
      } else if (text.match(/(\d+)\s*(?:hour|hr|h)/i)) {
        const hourMatch = text.match(/(\d+)\s*(?:hour|hr|h)/i);
        hoursAhead = parseInt(hourMatch![1]);
        timePeriod = `${hoursAhead} hour${hoursAhead > 1 ? 's' : ''}`;
      } else if (text.match(/(\d+)\s*(?:day|days|d)/i)) {
        const dayMatch = text.match(/(\d+)\s*(?:day|days|d)/i);
        hoursAhead = parseInt(dayMatch![1]) * 24;
        timePeriod = `${dayMatch![1]} day${parseInt(dayMatch![1]) > 1 ? 's' : ''}`;
      } else if (text.includes('next') || text.includes('after')) {
        hoursAhead = 24; // Default to 24 hours
        timePeriod = '24 hours';
      }
      
      if (coinSymbol) {
        // Get current price and provide expert prediction
        try {
          const quoteResponse = await cryptoAPI.getQuotes(coinSymbol);
          if (quoteResponse.data?.data?.[coinSymbol]) {
            const coinData = Array.isArray(quoteResponse.data.data[coinSymbol]) 
              ? quoteResponse.data.data[coinSymbol][0] 
              : quoteResponse.data.data[coinSymbol];
            
            const currentPrice = coinData.quote?.USD?.price || 0;
            const change24h = coinData.quote?.USD?.percent_change_24h || 0;
            const volume24h = coinData.quote?.USD?.volume_24h || 0;
            const marketCap = coinData.quote?.USD?.market_cap || 0;
            const coinName = coinData.name || coinSymbol;
            
            if (currentPrice > 0) {
              // Calculate expert prediction based on current trend
              const volatility = Math.abs(change24h);
              const trend = change24h > 0 ? 'bullish' : change24h < 0 ? 'bearish' : 'neutral';
              
              // Expert prediction calculation
              let predictedChange = 0;
              let confidence = 70;
              
              if (hoursAhead > 0) {
                // Short-term prediction (hours)
                if (trend === 'bullish' && volatility > 5) {
                  predictedChange = (change24h / 24) * hoursAhead * 0.7; // Momentum continuation with decay
                  confidence = Math.min(75, 65 + volatility * 0.5);
                } else if (trend === 'bearish' && volatility > 5) {
                  predictedChange = (change24h / 24) * hoursAhead * 0.7;
                  confidence = Math.min(75, 65 + volatility * 0.5);
                } else {
                  predictedChange = (change24h / 24) * hoursAhead * 0.5; // Lower momentum for neutral
                  confidence = 60;
                }
              } else {
                // Default 24-hour prediction
                predictedChange = change24h * 0.6; // Momentum continuation with decay
                confidence = Math.min(75, 65 + volatility * 0.3);
              }
              
              const predictedPrice = currentPrice * (1 + predictedChange / 100);
              const priceChange = ((predictedPrice - currentPrice) / currentPrice * 100);
              
              let response = `${coinName} (${coinSymbol}) Price Prediction - ${timePeriod || 'Next 24 Hours'}\n\n`;
              response += `**Current Price:** $${currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })}\n`;
              response += `**24h Change:** ${change24h > 0 ? '+' : ''}${change24h.toFixed(2)}%\n`;
              response += `**Market Cap:** $${(marketCap / 1e9).toFixed(2)}B\n\n`;
              
              response += `**Expert Prediction:**\n`;
              response += `• Expected Price: $${predictedPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })}\n`;
              response += `• Expected Change: ${priceChange > 0 ? '+' : ''}${priceChange.toFixed(2)}%\n`;
              response += `• Confidence: ${confidence.toFixed(0)}%\n`;
              response += `• Trend: ${trend.toUpperCase()}\n\n`;
              
              if (confidence > 70) {
                response += `**Trading Recommendation:**\n`;
                if (priceChange > 2) {
                  response += `✅ Strong buy signal. Entry: Current price or 2-3% dip. Stop: 5% below entry. Target: ${predictedPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })}`;
                } else if (priceChange < -2) {
                  response += `⚠️ Bearish outlook. Consider shorting or wait for better entry. Stop: 5% above entry if shorting.`;
                } else {
                  response += `➡️ Neutral/consolidation expected. Wait for breakout confirmation before entering.`;
                }
              } else {
                response += `**Trading Recommendation:**\n`;
                response += `⚠️ Moderate confidence. Use smaller position size (1-2% of portfolio). Wait for additional confirmation signals.`;
              }
              
              response += `\n\n**Note:** For detailed analysis, create a formal prediction in "New Prediction" page.`;
              
              return response;
            }
          }
        } catch (error) {
          return `${userName}, I couldn't fetch current data for ${coinSymbol}. Please check the coin symbol and try again, or create a prediction in "New Prediction" page for detailed analysis.`;
        }
      } else {
        return `${userName}, I need the coin symbol to provide a prediction. Please specify which coin (e.g., "predict BTC price" or "PI coin prediction"). You can also create a detailed prediction in "New Prediction" page.`;
      }
    }
    
    // Direct question matching - answer exactly what's asked
    // Use API dynamically, no static coin names
    if (text.includes('price') && (text.includes('what') || text.includes('current') || text.includes('now'))) {
      // Try to extract coin symbol dynamically
      const coinMatch = text.match(/\b([A-Z]{2,10})\b/);
      if (coinMatch) {
        try {
          const quoteResponse = await cryptoAPI.getQuotes(coinMatch[1].toUpperCase());
          if (quoteResponse.data?.data?.[coinMatch[1].toUpperCase()]) {
            const coinData = Array.isArray(quoteResponse.data.data[coinMatch[1].toUpperCase()]) 
              ? quoteResponse.data.data[coinMatch[1].toUpperCase()][0] 
              : quoteResponse.data.data[coinMatch[1].toUpperCase()];
            const price = coinData.quote?.USD?.price || 0;
            const coinName = coinData.name || coinMatch[1].toUpperCase();
            return `${userName}, ${coinName} (${coinMatch[1].toUpperCase()}) current price: $${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })}`;
          }
        } catch (error) {
          // Fall through to GPT-4
        }
      }
      return `${userName}, you can check current cryptocurrency prices in the Dashboard's "Live Prices" table. Which coin?`;
    }
    
    if ((text.includes('how') && text.includes('create')) || (text.includes('how') && text.includes('make'))) {
      if (text.includes('prediction')) {
        return `${userName}, to create a prediction:\n\n1. Go to "New Prediction" in the sidebar\n2. Select a cryptocurrency\n3. Choose a target date\n4. Submit - AI will analyze and provide predictions`;
      }
      if (text.includes('alert')) {
        return `${userName}, to create an alert:\n\n1. Go to "Alerts" in the sidebar\n2. Click "Create New Alert"\n3. Select a coin and set target price\n4. Choose "above" or "below"\n5. Submit`;
      }
    }
    
    if (text.includes('where') && (text.includes('find') || text.includes('see') || text.includes('check'))) {
      if (text.includes('price')) {
        return `${userName}, find prices in:\n• Dashboard → Live Prices table\n• Home page → Live Prices section\n• Click any coin for details`;
      }
      if (text.includes('prediction')) {
        return `${userName}, find your predictions in:\n• Dashboard → Recent Predictions\n• Sidebar → "Predictions" page`;
      }
      if (text.includes('alert')) {
        return `${userName}, find your alerts in:\n• Dashboard → Active Alerts\n• Sidebar → "Alerts" page`;
      }
    }
    
    if (text.includes('what') && text.includes('is')) {
      if (text.includes('spot')) {
        return `${userName}, spot trading is buying and holding actual cryptocurrency. You own the coins. No leverage, lower risk.`;
      }
      if (text.includes('futures')) {
        return `${userName}, futures trading is trading contracts with leverage. You don't own the coins, just bet on price direction. Higher risk, higher reward.`;
      }
      if (text.includes('prediction')) {
        return `${userName}, a prediction is an AI-analyzed forecast of a coin's future price based on market data and technical analysis.`;
      }
      if (text.includes('alert')) {
        return `${userName}, an alert is a notification you set to get an email when a coin's price reaches your target (above or below).`;
      }
    }
    
    if (text.includes('should') || text.includes('can i')) {
      if (text.includes('buy') || text.includes('sell')) {
        // Extract coin symbol dynamically from text
        const coinMatch = text.match(/\b([A-Z]{2,10})\b/);
        if (coinMatch) {
          const coinSymbol = coinMatch[1].toUpperCase();
          const userPreds = userData.predictions.filter(p => 
            p.coin_symbol.toUpperCase() === coinSymbol
          );
          if (userPreds.length > 0) {
            const latestPred = userPreds[0];
            const confidence = parseFloat(latestPred.confidence_score);
            if (confidence > 70) {
              return `${userName}, based on your ${coinSymbol} prediction (${confidence}% confidence), yes - this is a good setup. Entry: current price or 5% dip. Stop: 7% below entry.`;
            } else {
              return `${userName}, your ${coinSymbol} prediction shows ${confidence}% confidence - moderate. Wait for better entry or use smaller position size.`;
            }
          }
          return `${userName}, I need to see your ${coinSymbol} prediction first. Create one in "New Prediction" and I'll give you specific buy/sell advice.`;
        }
        // If no coin symbol found, let GPT-4 handle it
        try {
          const gptResponse = await chatbotAPI.query(originalText);
          if (gptResponse.data?.response) {
            return gptResponse.data.response;
          }
        } catch (error) {
          console.error('GPT API error:', error);
        }
        return `${userName}, which coin? Create a prediction first, then I'll tell you if you should buy or sell based on the analysis.`;
      }
    }
    
    // Better handling for "can you tell me" / "can you" questions
    if (text.includes('can you') || text.includes('tell me')) {
      // Extract the actual question part
      const questionPart = text.replace(/can you (tell me )?/i, '').trim();
      
      // Check for prediction count questions
      if (questionPart.includes('prediction') && (questionPart.includes('how many') || questionPart.includes('how much') || questionPart.includes('count') || questionPart.includes('did'))) {
        const totalPreds = userData.predictions.length;
        const activePreds = userData.predictions.filter(p => {
          const targetDate = new Date(p.target_date);
          return targetDate > new Date();
        });
        return `${userName}, you have created ${totalPreds} prediction${totalPreds !== 1 ? 's' : ''} in total.\n\nActive predictions: ${activePreds.length}`;
      }
      
      // Check for alert count questions
      if (questionPart.includes('alert') && (questionPart.includes('how many') || questionPart.includes('how much') || questionPart.includes('count') || questionPart.includes('did'))) {
        const totalAlerts = userData.alerts.length;
        const activeAlerts = userData.alerts.filter(a => a.status === 'active').length;
        return `${userName}, you have created ${totalAlerts} alert${totalAlerts !== 1 ? 's' : ''} in total.\n\nActive alerts: ${activeAlerts}`;
      }
      
      // Check for candle prediction count
      if (questionPart.includes('candle') && (questionPart.includes('how many') || questionPart.includes('how much') || questionPart.includes('count') || questionPart.includes('did'))) {
        const candleCount = userData.candlePredictions.length;
        return `${userName}, you have created ${candleCount} candle prediction${candleCount !== 1 ? 's' : ''}.`;
      }
    }
    
    // Fix specific common questions first
    // Current price queries - use API dynamically
    if ((text.includes('current price') || text.includes('price of') || text.includes('price for')) && 
        text.match(/\b([A-Z]{2,10})\b/) || text.includes('coin') || text.includes('crypto')) {
      // Extract coin symbol dynamically
      let coinSymbol = '';
      const coinMatch = text.match(/\b([A-Z]{2,10})\b/);
      if (coinMatch) {
        coinSymbol = coinMatch[1].toUpperCase();
      } else {
        // Extract from text patterns
        const words = text.split(/\s+/);
        for (const word of words) {
          const cleanWord = word.toLowerCase().replace(/[^a-z]/g, '');
          if (cleanWord.length >= 2 && cleanWord.length <= 15 && 
              !['price', 'coin', 'crypto', 'current', 'the', 'of', 'for'].includes(cleanWord)) {
            coinSymbol = cleanWord.toUpperCase();
            break;
          }
        }
      }
      
      if (coinSymbol && coinSymbol.length >= 2) {
        try {
          const quoteResponse = await cryptoAPI.getQuotes(coinSymbol);
          if (quoteResponse.data?.data?.[coinSymbol]) {
            const coinData = Array.isArray(quoteResponse.data.data[coinSymbol]) 
              ? quoteResponse.data.data[coinSymbol][0] 
              : quoteResponse.data.data[coinSymbol];
            const price = coinData.quote?.USD?.price || 0;
            const change24h = coinData.quote?.USD?.percent_change_24h || 0;
            const coinName = coinData.name || coinSymbol;
            return `${userName}, ${coinName} (${coinSymbol}) current price: $${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })}\n\n24h Change: ${change24h > 0 ? '+' : ''}${change24h.toFixed(2)}%`;
          }
        } catch (error) {
          // If API fails, let GPT-4 handle it (it knows about coins)
          // Fall through to GPT-4
        }
      }
    }
    
    // Check alerts (not count, but actual check)
    if (text.includes('check') && text.includes('alert')) {
      const activeAlerts = userData.alerts.filter(a => a.status === 'active');
      if (activeAlerts.length === 0) {
        return `${userName}, you don't have any active alerts. Create one in the "Alerts" page to get notified when prices reach your target.`;
      }
      let response = `${userName}, here are your active alerts:\n\n`;
      activeAlerts.slice(0, 5).forEach(alert => {
        response += `• ${alert.coin_name} (${alert.coin_symbol}): Alert when price goes ${alert.alert_type} $${parseFloat(alert.target_price).toLocaleString()}\n`;
      });
      return response;
    }
    
    // Change/edit alerts
    if ((text.includes('change') || text.includes('edit') || text.includes('modify')) && text.includes('alert')) {
      return `${userName}, to change an alert:\n\n1. Go to "Alerts" page in the sidebar\n2. Find the alert you want to modify\n3. Delete the old alert\n4. Create a new alert with your updated settings\n\nNote: Currently, alerts can't be edited directly. You need to delete and recreate them.`;
    }
    
    // Historical prices
    if (text.includes('historic') && (text.includes('price') || text.includes('check') || text.includes('view'))) {
      return `${userName}, to check historical prices:\n\n1. Go to Dashboard → "Historical Prices" in the sidebar\n2. Select a cryptocurrency\n3. Choose search mode: Single Date & Time or Date Range\n4. Click "Search Price" or "Search Range"\n\nYou can view prices from the last 2 years (730 days).`;
    }
    
    // Coin comparison
    if (text.includes('comparison') || (text.includes('compare') && text.includes('coin'))) {
      return `${userName}, to compare coins:\n\n1. Go to Dashboard → "Coin Comparison" in the sidebar\n2. Use the coin selector to add cryptocurrencies\n3. View side-by-side comparison of prices, market cap, volume, and 24h changes\n\nYou can compare multiple coins at once.`;
    }
    
    // Futures trading questions - use GPT-4 (it knows about all coins)
    if (text.includes('futures') && (text.includes('trade') || text.includes('trading'))) {
      // Use GPT for detailed futures advice (it knows about all coins)
      try {
        const gptResponse = await chatbotAPI.query(originalText);
        if (gptResponse.data?.response) {
          return gptResponse.data.response;
        }
      } catch (error) {
        console.error('GPT API error:', error);
        return `${userName}, for futures trading:\n\n**Quick Strategy:**\n• Use 3-5x leverage maximum\n• Set stop-loss at 2-3% from entry\n• Take profits at 5-10% targets\n• Monitor volume and trend confirmation\n\nFor detailed analysis, create a prediction or candle analysis.`;
      }
    }
    
    // If question doesn't match any pattern, use GPT-4 for out-of-box questions
    if (originalText.includes('?') || 
        text.startsWith('what') || 
        text.startsWith('how') || 
        text.startsWith('when') || 
        text.startsWith('where') || 
        text.startsWith('why') || 
        text.startsWith('should') ||
        text.startsWith('can') ||
        text.startsWith('will') ||
        text.length > 20) { // Longer questions likely need GPT
      
      // Check if it's asking about user's data (handle locally)
      if (text.includes('my') && (text.includes('prediction') || text.includes('alert') || text.includes('data'))) {
        const totalPreds = userData.predictions.length;
        const activePreds = userData.predictions.filter(p => {
          const targetDate = new Date(p.target_date);
          return targetDate > new Date();
        });
        const alertCount = userData.alerts.filter(a => a.status === 'active').length;
        const totalAlerts = userData.alerts.length;
        const candleCount = userData.candlePredictions.length;
        
        return `${userName}, here's your data:\n\n• Total Predictions: ${totalPreds} (${activePreds.length} active)\n• Total Alerts: ${totalAlerts} (${alertCount} active)\n• Candle Analyses: ${candleCount}\n\nWant more details on any of these?`;
      }
      
      // Use GPT-4 for out-of-box questions
      try {
        const gptResponse = await chatbotAPI.query(originalText);
        if (gptResponse.data?.response) {
          return gptResponse.data.response;
        }
      } catch (error) {
        console.error('GPT API error:', error);
      }
      
      return `${userName}, I want to answer your exact question: "${originalText}"\n\nCould you be more specific? For example:\n• "What is spot trading?"\n• "How to create a prediction?"\n• "Where can I see my alerts?"\n• "Should I buy BTC?"\n• "Show my portfolio"`;
    }
    
    // If it's a statement or any other question, use GPT-4
    // This catches everything that didn't match above patterns
    try {
      const gptResponse = await chatbotAPI.query(originalText);
      if (gptResponse.data?.response) {
        return gptResponse.data.response;
      }
    } catch (error) {
      console.error('GPT API error:', error);
      // Fallback only if GPT fails
      if (originalText.length > 10) {
        return `${userName}, I'm having trouble processing that. Could you rephrase your question? For example:\n• "What is the price of BTC?"\n• "Show my portfolio"\n• "How to create a prediction?"`;
      }
    }
    
    return `${userName}, I want to help you with exactly what you need.\n\nPlease ask a specific question, for example:\n• "Show my portfolio"\n• "What is spot trading?"\n• "How to create a prediction?"\n• "Should I buy BTC?"\n• "When to take profits?"`;
    };

  const handleClearHistory = () => {
    setMessages([]);
    try {
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem(getStorageKey());
      }
    } catch (err) {
      console.error('Failed to clear chatbot history from session:', err);
    }
    };

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Convert markdown to HTML
  const markdownToHtml = (text: string): string => {
    if (!text) return '';
    
    let html = text;
    
    // Escape HTML first to prevent XSS
    html = html
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    
    // Convert **bold** to <strong>bold</strong> (handle multiple on same line)
    html = html.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>');
    
    // Convert *italic* to <em>italic</em> (but not if it's part of **)
    html = html.replace(/(?<!\*)\*([^*\n]+?)\*(?!\*)/g, '<em>$1</em>');
    
    // Convert line breaks to <br />
    html = html.replace(/\n/g, '<br />');
    
    return html;
  };

  return (
    <div className="chatbot-container">
      {/* Chatbot Button */}
      <button
        className={`chatbot-button ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Open chatbot"
      >
        <div className="chatbot-icon">
          {isOpen ? (
            <i className="fas fa-times"></i>
          ) : (
            <>
              <i className="fas fa-comments"></i>
              <span className="notification-badge"></span>
            </>
          )}
        </div>
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="chatbot-window">
          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-info">
              <div className="chatbot-avatar">
                <i className="fas fa-robot"></i>
              </div>
              <div>
                <h3>Trading Advisor</h3>
                <p className="chatbot-status">
                  <span className="status-dot"></span>
                  {user ? `${user.first_name || user.username || 'Trader'}'s Advisor` : 'Online'}
                </p>
              </div>
            </div>
            <div className="chatbot-header-actions">
              <button
                className="chatbot-clear"
                onClick={handleClearHistory}
                aria-label="Clear chat history"
              >
                <i className="fas fa-trash"></i>
              </button>
            <button
              className="chatbot-minimize"
              onClick={() => setIsOpen(false)}
              aria-label="Minimize chatbot"
            >
              <i className="fas fa-minus"></i>
            </button>
            </div>
          </div>

          {/* Messages */}
          <div className="chatbot-messages">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`message ${message.sender === 'user' ? 'user-message' : 'bot-message'}`}
              >
                {message.sender === 'bot' && (
                  <div className="message-avatar">
                    <i className="fas fa-robot"></i>
                  </div>
                )}
                <div className="message-content">
                  {message.sender === 'bot' ? (
                    <p dangerouslySetInnerHTML={{ __html: markdownToHtml(message.text) }}></p>
                  ) : (
                    <p>{message.text}</p>
                  )}
                  <span className="message-time">{formatTime(message.timestamp)}</span>
                </div>
                {message.sender === 'user' && (
                  <div className="message-avatar user-avatar">
                    <i className="fas fa-user"></i>
                  </div>
                )}
              </div>
            ))}
            
            {/* Typing Indicator */}
            {isTyping && (
              <div className="message bot-message">
                <div className="message-avatar">
                  <i className="fas fa-robot"></i>
                </div>
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form className="chatbot-input-form" onSubmit={handleSendMessage}>
            <input
              ref={inputRef}
              type="text"
              className="chatbot-input"
              placeholder="Type your message..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
            />
            <button
              type="submit"
              className="chatbot-send-button"
              disabled={!inputMessage.trim()}
            >
              <i className="fas fa-paper-plane"></i>
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default Chatbot;

